import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { Section } from '~/eds/src/components/Section/Section';
import { Select } from '~/eds/src/components/Select/Select';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Nullable } from '~/eds/src/types/Nullable';
import { Option } from '~/eds/src/types/Option';
import { DataFieldType } from '~/enums/DataFieldType';
import { DocumentInformationField } from '~/features/search/types/bulk-actions';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

import { BulkEditFieldType } from '../types';
import { FieldEdit } from './FieldEdit';

type RadioGroupType = Nullable<BulkEditFieldType>;

const getRadioGroupOptions = (
  isDeleteEnabled: boolean,
  isAttachmentFieldSelected: boolean,
  intl: IntlShape,
): Option<RadioGroupType>[] => [
  {
    label: intl.formatMessage({
      id: 'EditFieldsAction.addReplaceOption',
      defaultMessage: 'Add to/Replacing existing values',
      description: 'Radio option for add/replace action',
    }),
    info: intl.formatMessage({
      id: 'EditFieldsAction.addReplaceInfo',
      defaultMessage:
        'Replacing will overwrite any data currently in the field and cannot be undone.',
      description: 'Information about add/replace action',
    }),
    value: 'add-replace',
    disabled: isAttachmentFieldSelected,
  },
  ...(isDeleteEnabled
    ? [
        {
          label: intl.formatMessage({
            id: 'EditFieldsAction.deleteOption',
            defaultMessage: 'Delete existing values',
            description: 'Radio option for delete action',
          }),
          info: intl.formatMessage({
            id: 'EditFieldsAction.deleteInfo',
            defaultMessage:
              'This will remove any data currently in the field and cannot be undone.',
            description: 'Information about delete action',
          }),
          value: 'delete' as const,
        },
      ]
    : []),
];

const getAddReplaceRadioOptions = (intl: IntlShape) => [
  {
    label: intl.formatMessage({
      id: 'EditFieldsAction.addToExistingValues',
      defaultMessage: 'Add to existing values',
      description: 'Radio option for adding to existing values',
    }),
    value: 'add',
  },
  {
    label: intl.formatMessage({
      id: 'EditFieldsAction.replaceExistingValues',
      defaultMessage: 'Replace existing values',
      description: 'Radio option for replacing existing values',
    }),
    value: 'replace',
  },
];

export type FieldEditValue = {
  value: any;
  keepOldValues?: boolean;
};
export type EditFieldData = {
  selectedFields: string[];
  actionType: RadioGroupType;
  fieldValueRecord: Record<string, FieldEditValue>;
};
interface Props {
  fields: Record<string, DocumentInformationField>;
  onChange: (params: EditFieldData) => void;
}
export const EditFieldsAction = ({ fields, onChange }: Props) => {
  const intl = useIntl();
  const isDeleteActionEnabled = useFlag(FlagType.BulkDeleteFieldValues);
  const [selectedFields, setSelectedField] = useState<string[]>([]);
  const [actionType, setActionType] = useState<RadioGroupType>(null);
  const [fieldValueRecord, setFieldValueRecord] = useState<
    Record<string, FieldEditValue>
  >({});

  const isAttachmentField = (field: string) =>
    fields[field].type === DataFieldType.ATTACHMENT;

  useEffect(() => {
    onChange({
      selectedFields,
      actionType,
      fieldValueRecord,
    });
  }, [fieldValueRecord, actionType, selectedFields]);

  const options = useMemo(() => {
    return Object.values(fields)
      .filter((field) => field.isEditable)
      .map((field) => ({ value: field.label, label: field.label }));
  }, [fields]);

  const selectedAttachmentFields = useMemo(
    () => selectedFields.filter(isAttachmentField),
    [selectedFields, fields],
  );

  const onFieldSelected = (updatedValue: string[] | null) => {
    const fieldsList = updatedValue ?? [];
    setSelectedField(fieldsList);

    const updatedFieldValueRecord = Object.fromEntries(
      fieldsList.map((field) => [field, fieldValueRecord[field] ?? {}]),
    );

    if (actionType === 'add-replace' && updatedValue?.some(isAttachmentField)) {
      setActionType(null);
    }

    setFieldValueRecord(updatedFieldValueRecord);
  };
  const onActionTypeChange = (updatedValue: RadioGroupType) => {
    setActionType(updatedValue);
    if (updatedValue === 'delete') {
      setFieldValueRecord({});
    }
  };

  const updateFieldValueRecord = (
    field: string,
    fieldEditValue: Partial<FieldEditValue>,
  ) => {
    setFieldValueRecord({
      ...fieldValueRecord,
      [field]: { ...fieldValueRecord[field], ...fieldEditValue },
    });
  };

  return (
    <Layout preset="subsections">
      <Section
        title={intl.formatMessage({
          id: 'EditFieldsAction.dataFieldsTitle',
          defaultMessage: 'Data Fields',
          description: 'Title for data fields section',
        })}
        description={intl.formatMessage({
          id: 'EditFieldsAction.dataFieldsDescription',
          defaultMessage: 'Select the data fields you want to edit.',
          description: 'Description for data fields selection',
        })}
      >
        <Select
          isMulti
          name="fields-select"
          onChange={onFieldSelected}
          value={selectedFields}
          options={options}
          enableSelectAll={false}
        />
        <RadioGroup
          name="action-type-radio"
          options={getRadioGroupOptions(
            isDeleteActionEnabled,
            selectedAttachmentFields.length > 0,
            intl,
          )}
          value={actionType}
          onChange={onActionTypeChange}
        />
        {selectedAttachmentFields.length > 0 && (
          <StatusMessage
            message={intl.formatMessage(
              {
                id: 'EditFieldsAction.attachmentFieldsWarning',
                defaultMessage:
                  'Can only delete the value in {fieldNames} {count, plural, one {field} other {fields}}.',
                description:
                  'Warning message for attachment fields. fieldNames is a comma-separated list of field names, count is the number of fields. Example: fieldNames = "Document Name", count = "5"',
              },
              {
                fieldNames: selectedAttachmentFields.join(', '),
                count: selectedAttachmentFields.length,
              },
            )}
          />
        )}
      </Section>
      {selectedFields.length > 0 &&
        actionType === 'add-replace' &&
        selectedFields.map((field) => (
          <Layout key={field} preset="form-fields">
            <FieldEdit
              field={fields[field]}
              onChange={(updatedValue: any) =>
                updateFieldValueRecord(field, {
                  value: updatedValue,
                })
              }
              value={fieldValueRecord[field]?.value}
            />
            {fields[field].type === DataFieldType.ARRAY_MULTIPLE && (
              <RadioGroup
                columns={2}
                name={`field-${field}-add-replace-radio`}
                value={
                  fieldValueRecord[field]?.keepOldValues === undefined
                    ? null
                    : fieldValueRecord[field]?.keepOldValues
                      ? 'add'
                      : 'replace'
                }
                options={getAddReplaceRadioOptions(intl)}
                onChange={(selectedValue) =>
                  updateFieldValueRecord(field, {
                    keepOldValues: selectedValue === 'add',
                  })
                }
              />
            )}
          </Layout>
        ))}
    </Layout>
  );
};
